import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLaunchDarklySettings } from "@MasterBluePrint/Store/Context/ContextSelector";
import { initialize, LDClient } from "launchdarkly-js-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

interface Props {
    featureFlags: string[];
}

let globalLDClient: LDClient | null = null;
let globalClientInitialized = false;

const generateGuestId = (): string =>
    crypto.getRandomValues(new Uint8Array(16)).reduce((id, byte) => id + byte.toString(16).padStart(2, "0"), "");

const getUserKey = (isAuthenticated: boolean | undefined, session: any, enableUserContext: boolean | undefined): string => {
    if (!enableUserContext) {
        return 'anonymous-user';
    }

    if (isAuthenticated && session?.userProfileId) {
        return session.userProfileId;
    }

    // Safely access localStorage only in the browser
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            let guestId = localStorage.getItem("ldGuestId");
            if (!guestId) {
                guestId = generateGuestId();
                localStorage.setItem("ldGuestId", guestId);
            }
            return guestId;
        }
        catch (error) {
            logger.warn("Failed to access localStorage:", error);
        }
    }

    // Fallback for server-side rendering
    // A stable, non-storing ID is needed here, or return a default value
    return 'anonymous-user';
};

const useLaunchDarklyFlags = ({ featureFlags }: Props): Record<string, boolean | undefined> => {
    const customLDSettings = useSelector((state: ApplicationState) => getLaunchDarklySettings(state));
    const [flagValues, setFlagValues] = useState<Record<string, boolean | undefined>>({});
    const onChangeHandlersRef = useRef<Record<string, (newValue: boolean) => void>>({});

    // Below code is commented as it is used in context which is commented at the moment. Once we start using contexts, this code to be uncommented.
    const session = useSelector((state: ApplicationState) => getSession(state));
    const isAuthenticated = session?.isAuthenticated;
    const userKey = useMemo(() => getUserKey(isAuthenticated, session, customLDSettings?.enableUserContext), [isAuthenticated, session, customLDSettings?.enableUserContext]);
    const stableFeatureFlagsKey = useMemo(() => featureFlags.slice().sort().join(","), [featureFlags]);

    useEffect(() => {
        let unsubscribed = false;

        const initAndEvaluate = async () => {
            if (!customLDSettings?.useLaunchDarkly || !customLDSettings?.launchDarklyClientId) {
                logger.error("LaunchDarkly client ID or feature toggle is missing.");
                return;
            }

            try {
                // ******** SENDING context with additional options is costing HUNDREDS per day.********
                // When we get licensing sorted out, we can look at turning context and options back on.

                if (!globalLDClient) {
                    if (!customLDSettings?.enableUserContext) {
                        globalLDClient = initialize(customLDSettings.launchDarklyClientId, { key: 'anonymous-user' });
                    }
                    else {
                        const context = { kind: "user", key: userKey, name: isAuthenticated ? session.email : "guest" };
                        const options = { evaluationReasons: true, sendEvents: true, logger: logger };
                        globalLDClient = initialize(customLDSettings.launchDarklyClientId, context, options);
                    }
                }
                // *************************************************************************************


                if (!globalClientInitialized) {
                    await new Promise(resolve => globalLDClient!.on("ready", resolve));
                    globalClientInitialized = true;
                }

                const updatedFlags: Record<string, boolean> = {};

                for (const flag of featureFlags) {
                    const value = await globalLDClient.variation(flag, false);
                    updatedFlags[flag] = value;

                    const handler = (newValue: boolean) => {
                        setFlagValues(prev => ({ ...prev, [flag]: newValue }));
                    };

                    globalLDClient.on(`change:${flag}`, handler);
                    onChangeHandlersRef.current[flag] = handler;
                }

                if (!unsubscribed) {
                    setFlagValues(updatedFlags);
                }
            } catch (error) {
                logger.error("LaunchDarkly initialization failed", error);
                const fallbackValues = featureFlags.reduce((acc, flag) => {
                    acc[flag] = false;
                    return acc;
                }, {} as Record<string, boolean>);

                if (!unsubscribed) {
                    setFlagValues(fallbackValues);
                }
            }
        };

        initAndEvaluate();

        return () => {
            unsubscribed = true;
            const client = globalLDClient;
            const handlers = onChangeHandlersRef.current;

            for (const flag of featureFlags) {
                if (handlers[flag]) {
                    client?.off(`change:${flag}`, handlers[flag]);
                }
            }
        };
    }, [stableFeatureFlagsKey, customLDSettings?.useLaunchDarkly, customLDSettings?.launchDarklyClientId]);
    // Dependency "userKey" must be added when context is used. At the moment context code is commented.
    // [stableFeatureFlagsKey, customLDSettings?.useLaunchDarkly, customLDSettings?.launchDarklyClientId, userKey]);

    return flagValues;
};

export default useLaunchDarklyFlags;
